import { childModelNames, modelNames } from '@/utils/commonEnum.js'

/*
 * 判断是否是第三方链接
 * */
export function isThirdLink(url) {
  return /^(https?:|http?:)/.test(url)
}

/*
 * 判断邮箱是否合法
 * */
export function emailValidate(email) {
  const emailStr = (email || '').toString().trim()
  if (!emailStr) return false

  const emailReg =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  return emailReg.test(emailStr)
}

/**
 * 兼容性强的日期格式化函数（支持苹果 Safari）
 * @param {string|Date} date - 日期字符串或 Date 对象
 * @param {string} fmt - 格式化模板，如 'yyyy-MM-dd hh:mm:ss'
 * @return {string}
 */
export function timeFormat(date, fmt = 'yyyy-MM-dd') {
  if (!date) return '' // 空值处理

  let dateObj

  if (date instanceof Date) {
    dateObj = date
  } else if (typeof date === 'string') {
    date = date.split('.')[0].trim() // 去掉小数部分

    if (/^\d{4}年\d{1,2}月\d{1,2}日/.test(date)) {
      const match = date.match(/(\d{4})年(\d{1,2})月(\d{1,2})日(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/)
      if (match) {
        const [_, y, M, d, h = 0, m = 0, s = 0] = match
        dateObj = new Date(y, M - 1, d, h, m, s)
      }
    } else if (/^\d{4}-\d{1,2}-\d{1,2}(?:\s+\d{1,2}:\d{1,2}(:\d{1,2})?)?$/.test(date)) {
      // Safari 不支持 2025-01-01 10:20:30，需要替换成 /
      date = date.replace(/-/g, '/')

      dateObj = new Date(date)
    } else if (/^\d{8}$/.test(date)) {
      // 解析 yyyyMMdd 格式
      const y = date.slice(0, 4)
      const M = date.slice(4, 6)
      const d = date.slice(6, 8)

      dateObj = new Date(y, M - 1, d) // 只传年月日，时分秒默认0
    } else if (/^\d{14}$/.test(date)) {
      // 兼容yyyyMMddhhmmss 格式转换
      const y = date.slice(0, 4)
      const M = date.slice(4, 6)
      const d = date.slice(6, 8)
      const h = date.slice(8, 10)
      const m = date.slice(10, 12)
      const s = date.slice(12, 14)

      dateObj = new Date(y, M - 1, d, h, m, s)
    } else {
      dateObj = new Date(date)
    }
  } else {
    return ''
  }

  if (isNaN(dateObj.getTime())) return '' // 非法日期
  const pad = num => (num < 10 ? '0' + num : '' + num)

  const replacements = {
    yyyy: dateObj.getFullYear(), // 4位年份
    yy: String(dateObj.getFullYear()).slice(-2), // 2位年份
    MM: pad(dateObj.getMonth() + 1), // 月份（补零，01-12）
    M: dateObj.getMonth() + 1, // 月份（1-12，不补零）
    dd: pad(dateObj.getDate()), // 日（补零，01-31）
    d: dateObj.getDate(), // 日（1-31，不补零）
    hh: pad(dateObj.getHours()), // 24小时制，补零（00-23）
    h: dateObj.getHours(), // 24小时制（0-23，不补零）
    mm: pad(dateObj.getMinutes()), // 分钟，补零（00-59）
    m: dateObj.getMinutes(), // 分钟（0-59，不补零）
    ss: pad(dateObj.getSeconds()), // 秒，补零（00-59）
    s: dateObj.getSeconds() // 秒（0-59，不补零）
  }

  return Object.keys(replacements)
    .sort((a, b) => b.length - a.length) // 长的优先
    .reduce((str, key) => str.replace(new RegExp(key, 'g'), replacements[key]), fmt)
}

export function timeFormatEn(originalDateStr) {
  if (!originalDateStr) return ''

  const validDateStr = originalDateStr.replace('：', ':')
  const date = new Date(validDateStr)

  const formatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', // 年
    month: 'long', // 月份全称
    day: 'numeric', // 日
    hour: '2-digit', // 小时
    minute: '2-digit', // 分钟
    hour12: false // 使用24小时制
  })

  const formattedPart = formatter.format(date)

  return formattedPart.replace(/, (\d{2}:\d{2})$/, ', at $1')
}

/**
 * 获取数据类型
 * */
export function getDataType(value) {
  if (value === null) {
    return 'null'
  }
  if (value === undefined) {
    return 'undefined'
  }

  const type = typeof value
  if (type !== 'object') {
    return type
  }
  if (Array.isArray(value)) {
    return 'array'
  }
  return 'object'
}

/*
 * 防抖函数
 */
export function debounce(func, wait, immediate = false) {
  let timeout

  const debounced = function (...args) {
    const context = this

    const later = () => {
      timeout = null
      if (!immediate) {
        func.apply(context, args)
      }
    }

    const callNow = immediate && !timeout

    clearTimeout(timeout)
    timeout = setTimeout(later, wait)

    if (callNow) {
      func.apply(context, args)
    }
  }

  debounced.cancel = () => {
    clearTimeout(timeout)
    timeout = null
  }

  return debounced
}

/*
 * 节流函数
 */
export function throttle(func, wait, options = {}) {
  let lastTime = 0
  let timeout
  let context
  let args

  options = { leading: true, trailing: false, ...options }
  const leading = options.leading
  const trailing = options.trailing

  const later = () => {
    lastTime = leading === false ? 0 : Date.now()
    timeout = null
    func.apply(context, args)
    if (!timeout) context = args = null
  }

  const throttled = function (...args) {
    const now = Date.now()
    if (!lastTime && leading === false) lastTime = now
    const remaining = wait - (now - lastTime)
    context = this

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      lastTime = now
      func.apply(context, args)
      if (!timeout) context = args = null
    } else if (!timeout && trailing) {
      timeout = setTimeout(later, remaining)
    }
  }

  throttled.cancel = () => {
    clearTimeout(timeout)
    lastTime = 0
    timeout = context = args = null
  }

  return throttled
}

/*
 * 深拷贝
 */
export function deepClone(obj, hash = new WeakMap()) {
  if (obj === null) return obj // 处理null
  if (typeof obj !== 'object') return obj // 处理基本类型
  if (hash.has(obj)) return hash.get(obj) // 防止循环引用

  let cloneObj
  if (obj instanceof Array) {
    cloneObj = []
    for (let i = 0; i < obj.length; i++) {
      cloneObj[i] = deepClone(obj[i], hash)
    }
  } else if (obj instanceof Object) {
    cloneObj = {}
    for (let key in obj) {
      if (Object.hasOwnProperty.call(obj, key)) {
        cloneObj[key] = deepClone(obj[key], hash)
      }
    }
  }

  hash.set(obj, cloneObj)
  return cloneObj
}

/*
 * 合并对象
 */
export function deepMerge(target, source) {
  // 创建目标对象的浅拷贝，避免直接修改原对象
  let output = Array.isArray(target) ? [...target] : { ...target }

  // 遍历源对象的所有自有属性
  Object.keys(source).forEach(key => {
    if (Object.hasOwnProperty.call(source, key)) {
      // 如果源对象的属性是对象且非null，且不是数组，则递归合并
      if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key])) {
        // 确保目标对象中也有此属性且为对象，避免错误的类型覆盖
        if (typeof target[key] === 'object' && target[key] !== null) {
          output[key] = deepMerge(target[key], source[key])
        } else {
          output[key] = deepMerge({}, source[key])
        }
      } else if (Array.isArray(source[key]) && Array.isArray(target[key])) {
        // 如果源和目标都是数组，则合并数组
        output[key] = target[key].concat(source[key])
      } else {
        // 否则，直接覆盖或新增属性
        output[key] = source[key]
      }
    }
  })

  return output
}

/**
 * 扁平数据转换树形结构
 * @param {*} data
 * @param option{
 *   setLeafToNull: false, // 是否把叶子节点设置为null
 *   id: 'id', // 节点id字段名
 *   pid: 'parentId', // 节点父id字段名
 *   children: 'children' // 节点子节点字段名
 * }
 * @returns
 */
export function convertToTree(data, option = {}) {
  const options = {
    setLeafToNull: false,
    id: 'id',
    pid: 'parentId',
    children: 'children',
    ...option
  }

  const tree = [] // 存储根节点
  const childrenOf = {} // 存储子节点映射

  // 构建映射表，并初始化根节点和子节点列表
  data.forEach(node => {
    childrenOf[node[options.id]] = childrenOf[node[options.id]] || []
    // 如果找不到父节点或者父节点ID为null或者0，视为根节点
    if (!childrenOf[node[options.pid]] || !node[options.pid]) {
      tree.push(node)
    } else {
      // 否则，将当前节点添加到其父节点的子节点列表
      childrenOf[node[options.pid]].push(node)
    }
  })

  // 递归函数，用于构建子树
  function buildChildren(nodes) {
    return nodes.map(node => {
      const childNodes = childrenOf[node[options.id]] || []
      const hasChildren = childNodes.length > 0

      return {
        ...node,
        [options.children]: hasChildren ? buildChildren(childNodes) : !options.setLeafToNull ? [] : null
      }
    })
  }

  // 使用递归函数从根节点开始构建整个树
  return buildChildren(tree)
}

/*
 * 获取rgba
 */
export function getRgba(hexOrRgba, alpha) {
  function hexToRgb(hex) {
    // 处理简写的hex颜色值
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
    hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b)

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        }
      : null
  }

  // 判断是否以及是rgba
  const rgbaRegex = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/
  const rgbaMatch = hexOrRgba.match(rgbaRegex)

  if (rgbaMatch) {
    // 如果是rgba格式，直接返回
    return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${alpha})`
  } else {
    // 如果不是，则将hex转换为rgb再返回
    const rgb = hexToRgb(hexOrRgba)
    if (rgb) {
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
    } else {
      throw new Error('Invalid color format')
    }
  }
}

/*
 * 获取序号 index从1开始
 */
export function getRank(index) {
  if (index === 0) return '--'
  index = Math.max(1, index)
  return index + (index <= 3 ? ['st', 'nd', 'rd'][index - 1] : 'th')
}

/*
 * 数字展示保留位数
 * @param {*} value
 * @param {*} minDigits 最少位数
 * @param {*} maxDigits 最多位数
 * @param {*} showPercent 是否显示百分比
 * @param {*} defaultShow 默认显示
 * @param {*} showSymbol 是否显示符号 + -
 */
export function getLocalString(value, minDigits = 1, maxDigits = 1, showPercent = true, defaultShow = '--', showSymbol = false) {
  if (value === null || value === undefined) {
    return defaultShow
  } else {
    let res = showSymbol ? (value > 0 ? '+' : '') : ''
    res += value.toLocaleString(undefined, {
      minimumFractionDigits: minDigits,
      maximumFractionDigits: maxDigits
    })
    return showPercent ? `${res}%` : res
  }
}

export async function downloadWithCustomName(url, filename) {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      return new Promise((resolve, reject) => {
        reject(new Error('下载失败'))
      })
    }

    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = blobUrl
    a.download = filename // 强制使用自定义名称
    document.body.appendChild(a)
    a.click()

    // 清理
    setTimeout(() => {
      document.body.removeChild(a)
      window.URL.revokeObjectURL(blobUrl)
    }, 100)
  } catch (error) {
    console.error('下载失败:', error)
    return new Promise((resolve, reject) => {
      reject(new Error('下载失败'))
    })
  }
}

/*
 * 判断是否为移动端平台
 */
export const isApp = window.navigator.userAgent.match(
  /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i
)

// 复制方法
export function copy(url) {
  const input = document.createElement('textarea')

  let textToCopy = url
  if (typeof textToCopy !== 'string') {
    textToCopy = String(textToCopy)
  }

  input.value = textToCopy
  document.body.appendChild(input)
  input.select()
  document.execCommand('Copy')
  document.body.removeChild(input)
}

// 判断是否为图片
const imageCheckCache = new Map()
const MAX_CACHE_SIZE = 50
const TTL = 1 * 60 * 1000 // 1 分钟
export function isImageByPreload(url) {
  const now = Date.now()

  if (imageCheckCache.has(url)) {
    const { value, time } = imageCheckCache.get(url)
    if (now - time < TTL) {
      return value
    }
    imageCheckCache.delete(url)
  }

  const promise = new Promise(resolve => {
    const img = new Image()
    let done = false

    const finish = result => {
      if (!done) {
        done = true
        resolve(result)
      }
    }

    img.onload = () => finish(true)
    img.onerror = () => finish(false)
    img.src = url

    setTimeout(() => finish(false), 300)
  })

  imageCheckCache.set(url, { value: promise, time: now })

  // 清理
  if (imageCheckCache.size > MAX_CACHE_SIZE) {
    const firstKey = imageCheckCache.keys().next().value
    imageCheckCache.delete(firstKey)
  }
  return promise
}

export async function replaceLinks(html) {
  const linkRegex = /<a href="(.*?)">(.*?)<\/a>/g
  const matches = [...html.matchAll(linkRegex)]

  const IMAGE_EXT_REG = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i

  if (!matches.length) return html

  const replacements = await Promise.all(
    matches.map(async match => {
      const [full, href, text] = match

      // 先用扩展名判断
      if (IMAGE_EXT_REG.test(href)) {
        return {
          full,
          replacement: `<img src="${href}" alt="${text}" style="max-width:200px;height:auto;cursor:pointer" onclick="window.open('${href}')"/>`
        }
      }

      // 再 preload
      const isImage = await isImageByPreload(href)
      return isImage
        ? {
            full,
            replacement: `<img src="${href}" alt="${text}" style="max-width:200px;height:auto;cursor:pointer" onclick="window.open('${href}')"/>`
          }
        : { full, replacement: full }
    })
  )

  // 一次性替换
  let result = html
  for (const { full, replacement } of replacements) {
    result = result.replace(full, replacement)
  }

  return result
}

// SEO内容摘要化（清洗 + 截断）
export function extractSummary(htmlContent, maxLength = 160) {
  if (!htmlContent) return ''

  // 去掉所有 HTML 标签
  const text = htmlContent.replace(/<[^>]+>/g, '').trim()

  // 压缩多余空格
  const clean = text.replace(/\s+/g, ' ')

  // 截断到 150～180 字最佳
  if (clean.length > maxLength) {
    return clean.slice(0, maxLength) + '...'
  }

  return clean
}

/**
 * prompt消耗计算公式
 * @param {number} questionCount - 问题数量
 * @param {number} modelCount - 模型系数
 * @param {string|Date} startDate - 开始日期
 * @param {string|Date} endDate - 结束日期
 * @param {number} rate - 百分比
 * @return {number}
 */
export function formatPrompt(questionCount, modelCount, startDate = '', endDate = '', rate) {
  // 空值判断
  if (!questionCount || !modelCount || !startDate || !endDate || Number.isNaN(rate)) return '--'
  // 订阅天数
  const start = new Date(startDate)
  const end = new Date(endDate)
  start.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
  // prompt消耗额度
  const promptValue = questionCount * modelCount * Number(rate)
  // 向下取 1 位小数 * 天数
  const value = (Math.floor(promptValue * 10) / 10) * days
  // 整数不显示小数
  return Number.isInteger(value) ? value : value.toFixed(1)
}

export function exportExcel(res, name) {
  // 小于 10KB 不下载
  if (!res || res.size < 1024) {
    return false
  }

  const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  if ('download' in document.createElement('a')) {
    // 非IE下载
    const link = document.createElement('a')
    link.download = name
    link.style.display = 'none'
    link.href = URL.createObjectURL(blob)
    document.body.appendChild(link)
    link.click()
    URL.revokeObjectURL(link.href) // 释放URL 对象
    document.body.removeChild(link)
  } else {
    // IE10+下载
    navigator.msSaveBlob(blob, name)
  }

  return true
}

// 计算文件大小
export const formatFileSize = size => {
  if (!size && size !== 0) return '--'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let index = 0

  while (size >= 1024 && index < units.length - 1) {
    size /= 1024
    index++
  }

  return `${size.toFixed(index === 0 ? 0 : 2)} ${units[index]}`
}

// 将模型列表转换为选择器选项
export function mapModelList(modelList = []) {
  return (modelList || []).map(item => {
    const { modelName, model, id } = item

    const modelInfo = modelNames.find(p => p.value === modelName)
    const childInfo = childModelNames.find(p => p.value === model)

    const childValue = childInfo?.value || model || ''
    const childLabel = childInfo?.label || model || ''

    const value = `${id}|${childValue}`

    // 有匹配 model 的情况
    if (modelInfo) {
      return {
        label: modelInfo.i18Key,
        child: childLabel,
        value
      }
    }

    return {
      label: 'common.allModels',
      value
    }
  })
}

/** 清洗文件名中的特殊字符，避免 OSS 直传失败 */
export function buildOssObjectName(originalName) {
  const lastDotIndex = originalName.lastIndexOf('.')
  const hasExt = lastDotIndex > 0
  const rawName = hasExt ? originalName.slice(0, lastDotIndex) : originalName
  const ext = hasExt ? originalName.slice(lastDotIndex) : ''
  const safeName = rawName.replace(/[^\w\u4e00-\u9fff.-]/g, '_').replace(/_+/g, '_') || 'file'

  return `${safeName}${ext}`
}
// 从 translations 中按 locale 读取指定 field
// 兼容两种形状：
// 1) { "zh-CN": { "name": "…" } }  多字段对象
// 2) { "zh-CN": "DeepSeek" }       单值字符串
// fallback：当前语言 → 短语言码(zh-CN→zh) → en-US → zh-CN
export function getTranslationField(json, locale, field) {
  let translations = json
  if (!translations) return ''
  if (typeof translations === 'string') {
    try {
      translations = JSON.parse(translations || '{}')
    } catch {
      return ''
    }
  }

  const loc = String(locale || '')
  const candidates = [loc]
  const shortLocale = loc.split('-')[0]
  if (shortLocale && shortLocale !== loc) candidates.push(shortLocale)
  if (!loc.startsWith('en')) candidates.push('en-US')
  if (!loc.startsWith('zh')) candidates.push('zh-CN')

  for (const locKey of candidates) {
    const localeData = translations?.[locKey]
    if (localeData == null || localeData === '') continue
    // 扁平结构：整段即文案
    if (typeof localeData === 'string') return localeData
    // 对象结构：取指定 field
    if (field && localeData[field]) return localeData[field]
  }

  return ''
}
